import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

export interface JobNote {
  id: string;
  jobId: string;
  category: string | null;
  content: string;
  createdById: string;
  createdByName: string | null;
  createdAt: string;
  senderType: 'staff' | 'client';
  clientVisible: boolean;
}

const jobNotesKeys = {
  all: (jobId: string) => ['jobs', jobId, 'notes'] as const,
};

function mapNote(raw: Record<string, unknown>): JobNote {
  return {
    id: raw.id as string,
    jobId: (raw.job_id || raw.jobId) as string,
    category: (raw.category ?? null) as string | null,
    content: raw.content as string,
    createdById: (raw.created_by_id || raw.createdById) as string,
    createdByName: (raw.created_by_name || raw.createdByName || null) as string | null,
    createdAt: (raw.created_at || raw.createdAt) as string,
    senderType: (raw.sender_type || raw.senderType || 'staff') as 'staff' | 'client',
    clientVisible: (raw.client_visible ?? raw.clientVisible ?? false) as boolean,
  };
}

export function useJobNotes(jobId: string) {
  return useQuery({
    queryKey: jobNotesKeys.all(jobId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>(
        `/jobs/${jobId}/notes`,
      );
      const raw = response.data.data ?? [];
      return raw.map(mapNote);
    },
    enabled: !!jobId,
  });
}

export function useCreateJobNote(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      category: string | null;
      content: string;
      reminderDate?: string;
      reminderAssigneeId?: string;
      clientVisible?: boolean;
    }) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        `/jobs/${jobId}/notes`,
        {
          category: payload.category,
          content: payload.content,
          reminder_date: payload.reminderDate || null,
          reminder_assignee_id: payload.reminderAssigneeId || null,
          client_visible: payload.clientVisible ?? false,
        },
      );
      return mapNote(response.data.data as Record<string, unknown>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: jobNotesKeys.all(jobId) });
    },
  });
}
